<template>
  <component
    :is="tag"
    :class="buttonClasses"
    :disabled="disabled"
    :type="tag === 'button' ? type : undefined"
    @click="$emit('click', $event)"
  >
    <slot />
  </component>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'outline', 'ghost', 'danger', 'success'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'button',
  },
  tag: {
    type: String,
    default: 'button',
  },
});

defineEmits(['click']);

const buttonClasses = computed(() => {
  return [
    'aim-button',
    `aim-button--${props.variant}`,
    `aim-button--${props.size}`,
  ];
});
</script>

<style scoped lang="scss">
@import '../../styles/design-system.scss';

.aim-button {
  @include button-base;
  font-weight: $font-weight-medium;
  
  &--primary {
    background: $primary-600;
    color: $text-inverse;
    
    &:hover:not(:disabled) {
      background: $primary-700;
      transform: translateY(-1px);
      box-shadow: $shadow-md;
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }
  
  &--secondary {
    background: $gray-200;
    color: $text-primary;
    
    &:hover:not(:disabled) {
      background: $gray-300;
    }
  }
  
  &--outline {
    background: transparent;
    color: $primary-600;
    border: 1.5px solid $primary-600;
    
    &:hover:not(:disabled) {
      background: $primary-50;
      border-color: $primary-700;
    }
  }
  
  &--ghost {
    background: transparent;
    color: $text-secondary;
    
    &:hover:not(:disabled) {
      background: $gray-100;
      color: $text-primary;
    }
  }
  
  &--danger {
    background: $error-500;
    color: $text-inverse;
    
    &:hover:not(:disabled) {
      background: $error-600;
    }
  }
  
  &--success {
    background: $success-600;
    color: $text-inverse;
    
    &:hover:not(:disabled) {
      background: $success-700;
    }
  }
  
  &--sm {
    padding: $spacing-1 $spacing-3;
    font-size: $font-size-xs;
  }
  
  &--md {
    padding: $spacing-2 $spacing-4;
    font-size: $font-size-sm;
  }
  
  &--lg {
    padding: $spacing-3 $spacing-6;
    font-size: $font-size-base;
  }
}
</style>

